import { useCallback, useEffect, useRef, useState } from "react";
import { getWorkspaceDetailApi } from "../../../api/workspaceApi";
import { mapWorkspaceToSavedSearchOption } from "../../Workspaces/savedSearchQuery";

const hasQuery = (option) => Boolean(option?.query &&
  typeof option.query === "object" && !Array.isArray(option.query));

/** Own detail requests by user intent, including retries and default selection. */
export default function useSavedSearchSelection({ savedSearchCatalog, onResolved }) {
  const generation = useRef(0);
  const retryOption = useRef(null);
  const onResolvedRef = useRef(onResolved);
  onResolvedRef.current = onResolved;
  const [savedSearchLoad, setSavedSearchLoad] = useState(null);

  useEffect(() => () => { generation.current += 1; }, []);

  const invalidateSavedSearch = useCallback(() => {
    generation.current += 1;
    retryOption.current = null;
    setSavedSearchLoad(null);
  }, []);

  const selectSavedSearch = useCallback(async (option) => {
    const request = ++generation.current;
    retryOption.current = null;
    setSavedSearchLoad("loading");
    let resolved = option;
    try {
      if (!hasQuery(option) && savedSearchCatalog === "workspace") {
        const id = option?.id || option?._id;
        if (!id) throw new Error("Saved search has no identity");
        const response = await getWorkspaceDetailApi(id, 1, 1);
        if (response?.status !== 200) throw new Error("Saved search detail unavailable");
        resolved = mapWorkspaceToSavedSearchOption(response.data);
        if (String(resolved?.id) !== String(id)) throw new Error("Saved search detail identity mismatch");
      }
      if (!hasQuery(resolved)) throw new Error("Saved search has no query");
    } catch (_error) {
      if (request !== generation.current) return;
      retryOption.current = option;
      setSavedSearchLoad("error");
      return;
    }
    if (request !== generation.current) return;
    setSavedSearchLoad(null);
    onResolvedRef.current(resolved);
  }, [savedSearchCatalog]);

  const retrySavedSearch = useCallback(() => {
    if (retryOption.current) return selectSavedSearch(retryOption.current);
  }, [selectSavedSearch]);

  return { selectSavedSearch, invalidateSavedSearch, savedSearchLoad, retrySavedSearch };
}
